import { useEffect, useRef, useState } from 'react'
import isEqual from 'lodash/isEqual'

import useDispatch from 'hooks/useDispatch'

import { updateFilters } from '../../../actions/searchActions'

import CommonMultiSelectFilter from '../common/MultiSelectFilter'

import FilterButton from './FilterButton'

type FilterKey = keyof GQL_SearchFilters

const useSelectedSlugsState = (initial: string[]) => {
  const [selectedSlugs, setSelectedSlugs] = useState(initial)

  const prevSelectedSlugs = useRef(initial)

  useEffect(() => {
    const selectionUpdated =
      isEqual(prevSelectedSlugs.current, initial) !== true

    prevSelectedSlugs.current = initial

    if (selectionUpdated) {
      setSelectedSlugs(initial)
    }
  }, [initial])

  return [selectedSlugs, setSelectedSlugs] as const
}

type Props = {
  label: string
  filterKey: FilterKey
  possibleOptions: { slug: string; name: string }[]
  selectedSlugs: string[]
}

const MultiSelectFilter = ({
  label,
  filterKey,
  possibleOptions = [],
  selectedSlugs: initialSelectedSlugs = [],
}: Props) => {
  const dispatch = useDispatch()

  const [selectedSlugs, setSelectedSlugs] =
    useSelectedSlugsState(initialSelectedSlugs)

  const handleSelectionChange = (nextFilters: GQL_SearchFilters) => {
    setSelectedSlugs(nextFilters[filterKey])
  }

  const isActive = selectedSlugs?.length > 0

  const handleApply = () => {
    dispatch(updateFilters({ [filterKey]: selectedSlugs }))
  }

  const handleClear = () => {
    setSelectedSlugs([])
    dispatch(updateFilters({ [filterKey]: null }))
  }

  const handleReset = () => {
    setSelectedSlugs(initialSelectedSlugs)
  }

  const dynamicLabel =
    selectedSlugs?.length > 0 ? `${selectedSlugs?.length} ${label}` : label

  return (
    <FilterButton
      isActive={isActive}
      onApply={handleApply}
      onClear={handleClear}
      onReset={handleReset}
      label={dynamicLabel}
    >
      <CommonMultiSelectFilter
        name={filterKey}
        valueKey={filterKey}
        values={selectedSlugs}
        items={possibleOptions}
        onChange={handleSelectionChange}
        noTruncate
      />
    </FilterButton>
  )
}

export default MultiSelectFilter
